import * as axios from "axios";
import { AlertMessage, AlertType } from "../types/AlertMessage";
import { CurrentUser } from "../types/User";
import { DateTime } from "luxon";

export default abstract class ApiClientFactory {
  public static Create(
    user: CurrentUser | undefined,
    logout: (redirectBack?: boolean) => void,
    showAlert: (message: AlertMessage) => void,
    navigateToMaintenance: () => void,
    abortController?: AbortController,
  ): axios.AxiosInstance {
    const client = axios.default.create({
      baseURL: process.env.REACT_APP_API_BASE_URL,
      timeout: 31000,
      signal: abortController?.signal,
      headers: {
        Accept: "application/json",
        authorization: user ? `Bearer ${user.token}` : undefined,
        "X-Tenant": process.env.REACT_APP_TENANT === "default" ? "datacutter" : process.env.REACT_APP_TENANT,
      },
    });

    const HasProxyInterference = (error: axios.AxiosError): boolean => {
      const responseHeaders = error.response
        ?.headers as axios.AxiosResponseHeaders;
      if (responseHeaders["x-maintained-by"] === "Basemap Ltd") {
        // The API returns a custom header - if this isn't present, the request has probably
        // been intercepted by a proxy or firewall.
        return false;
      }

      return true;
    };

    const parseDates = (obj: any) => {
      if (typeof obj !== "object" || obj === null) {
        return obj;
      }

      for (const key in obj) {
        const isoDateRegex =
          /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/;
        if (typeof obj[key] === "string" && `${obj[key]}`.match(isoDateRegex)) {
          const dateTime = DateTime.fromISO(obj[key], {
            zone: "utc",
          }).toLocal();
          if (dateTime.isValid) {
            obj[key] = dateTime;
          }
        } else if (typeof obj[key] === "object") {
          parseDates(obj[key]);
        }
      }
      return obj;
    };

    client.interceptors.response.use(
      (response) => {
        response.data = parseDates(response.data);
        return response;
      },
      (error: axios.AxiosError) => {
        if (error.code === "ERR_CANCELED") {
          return;
        } else if (error.response?.status === 503) {
          const isAdmin = user?.roles.includes("DataCutter Administrator");
          if (
            !isAdmin &&
            window.location.pathname !== "/maintenance" &&
            !window.location.pathname.includes("/admin/login")
          ) {
            navigateToMaintenance();
          }
        } else if (HasProxyInterference(error)) {
          showAlert({
            message:
              "Request Failed - detected possible proxy of firewall intercepting the request",
            type: AlertType.Error,
          });
        } else if (
          error.response?.status === 401 &&
          error.request.responseURL.toLowerCase().indexOf("account/logout") ===
            -1
        ) {
          logout();
        } else {
          //@ts-ignore - suppressAlert isn't defined on the Axios Config interface
          const suppressAlert = error.config?.suppressAlert ?? false;
          if (
            !suppressAlert &&
            error.request.responseURL.toLowerCase().indexOf("/login") !== -1 &&
            error.response?.status !== 401
          ) {
            showAlert({
              message:
                (
                  !!error.response?.data &&
                  typeof error.response.data === "string"
                ) ?
                  (error.response.data as string)
                : error.message,
              type: AlertType.Error,
            });
          }
        }

        throw error;
      },
    );

    return client;
  }
}
